import { DashboardApiFilters, fetchDashboardEndpoint, requestDashboardExport } from "../dashboardApi";

export type SalesPeriod = "by_week" | "by_month" | "by_year";

export type ConversionKey = "lead" | "pipeline" | "deal";

export interface SalesOverviewStat {
  key: string;
  displayName: string;
  amount: number | null;
  count: number | null;
  growth: string;
}

export interface SalesTrendPoint {
  date: string;
  actual: number;
  prior: number;
}

export interface PipelineStage {
  name: string;
  value: number;
  dealCount: number;
}

export interface RecentDeal {
  dealId: number | null;
  title: string;
  value: number;
  status: string;
  ownerName: string;
  ownerAvatarUrl: string | null;
}

export interface CountWithGrowth {
  count: number;
  growth: string;
}

export interface SalesOverview {
  overviewStats: SalesOverviewStat[];
  conversionRate: Record<ConversionKey, number>;
  dealsWonVsLost: { won: CountWithGrowth; lost: CountWithGrowth };
  pipeline: { total: number; dealCount: number; stages: PipelineStage[] };
  recentDeals: RecentDeal[];
  avgDeal: { current: number; previous: number; growth: string };
  salesGrowth: Record<SalesPeriod, SalesTrendPoint[]>;
}

export const SALES_PERIODS: SalesPeriod[] = ["by_week", "by_month", "by_year"];

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const toNumber = (value: unknown) => {
  const parsed = typeof value === "string" ? parseFloat(value.replace(/[^0-9.-]/g, "")) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value: unknown) => (value === undefined || value === null || value === "" ? null : toNumber(value));

const toText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const growthOf = (raw: any) => {
  if (!raw || typeof raw !== "object") return "";
  const key = Object.keys(raw).find((field) => /growth_rate(_by_last_period)?$/.test(field));
  return key ? toText(raw[key]) : "";
};

const normalizeTrend = (raw: unknown): SalesTrendPoint[] =>
  asArray(raw).map((point) => ({
    date: toText(point?.date),
    actual: toNumber(point?.actual),
    prior: toNumber(point?.prior ?? point?.previous),
  }));

const normalizeCountWithGrowth = (raw: any): CountWithGrowth => ({
  count: toNumber(raw?.count_in_current_period ?? raw?.count),
  growth: toText(raw?.count_growth_rate_by_last_period ?? raw?.growth_rate),
});

export const normalizeSalesOverview = (raw: any): SalesOverview => {
  const wonLost = raw?.deals_won_vs_lost ?? {};
  const pipeline = raw?.sales_pipeline_overview ?? {};
  const conversion = raw?.conversion_rate ?? {};
  const avgDeal = raw?.avg_deal_stats ?? {};

  return {
    overviewStats: asArray(raw?.overview_stats).map((stat) => ({
      key: toText(stat?.key),
      displayName: toText(stat?.display_name ?? stat?.key),
      amount: toOptionalNumber(stat?.amount ?? stat?.value),
      count: toOptionalNumber(stat?.count),
      growth: growthOf(stat),
    })),
    conversionRate: {
      lead: toNumber(conversion?.by_lead?.conversion_rate_in_current_period),
      pipeline: toNumber(conversion?.by_pipeline?.conversion_rate_in_current_period),
      deal: toNumber(conversion?.by_deal?.conversion_rate_in_current_period),
    },
    dealsWonVsLost: {
      won: normalizeCountWithGrowth(wonLost?.won),
      lost: normalizeCountWithGrowth(wonLost?.lost),
    },
    pipeline: {
      total: toNumber(pipeline?.total_sales ?? pipeline?.total),
      dealCount: toNumber(pipeline?.total_deal_count ?? pipeline?.deal_count),
      stages: asArray(pipeline?.stages).map((stage, index) => ({
        name: toText(stage?.stage_name ?? stage?.name) || `Stage ${index + 1}`,
        value: toNumber(stage?.sales_amount ?? stage?.value ?? stage?.amount),
        dealCount: toNumber(stage?.deal_count ?? stage?.count),
      })),
    },
    recentDeals: asArray(raw?.recently_created_deals).map((deal) => ({
      dealId: toOptionalNumber(deal?.deal_id ?? deal?.id),
      title: toText(deal?.title ?? deal?.deal_name),
      value: toNumber(deal?.value ?? deal?.deal_value),
      status: toText(deal?.status),
      ownerName: toText(deal?.owner?.name ?? deal?.owner?.owner_name),
      ownerAvatarUrl: deal?.owner?.avatar_url ?? deal?.owner?.owner_avatar_url ?? null,
    })),
    avgDeal: {
      current: toNumber(avgDeal?.avg_deal_value_in_current_period),
      previous: toNumber(avgDeal?.avg_deal_value_by_last_period ?? avgDeal?.avg_deal_value_from_last_period),
      growth: toText(avgDeal?.avg_deal_value_growth_rate_by_last_period),
    },
    salesGrowth: SALES_PERIODS.reduce(
      (acc, period) => ({ ...acc, [period]: normalizeTrend(raw?.sales_growth?.[period]) }),
      {} as Record<SalesPeriod, SalesTrendPoint[]>
    ),
  };
};

export const fetchSalesOverview = async (
  tenantId: string | number,
  filters: DashboardApiFilters = {}
): Promise<SalesOverview> =>
  normalizeSalesOverview(
    await fetchDashboardEndpoint("/dashboard/sales-overview/", tenantId, filters, "sales overview")
  );

export const exportSalesOverviewExcel = (tenantId: string | number, filters: DashboardApiFilters = {}) =>
  requestDashboardExport("/dashboard/sales-overview/export/", tenantId, filters, "sales overview");
